import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
  inject,
  DestroyRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { UserData } from '@chefami-monorepo/types';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImagePaths } from '../../constants/global';
import { StepPreconditionService } from '../../services/step-precondition.service';

interface CheckoutStep {
  id: number;
  label: string;
  icon: string;
  route?: string;
  width: number;
  height: number;
}

@Component({
  selector: 'app-header',
  imports: [NgOptimizedImage],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stepPrecondition = inject(StepPreconditionService);
  options = input.required<UserData>();

  // Computed values for easy template access
  isLoggedIn = computed(() => this.options().isLoggedIn);
  userInfo = computed(() => this.options().userInfo);
  siteInfo = computed(() => this.options().siteInfo);

  // Computed for logo URL from siteInfo
  logoUrl = computed(() => this.siteInfo()?.site_url || '/');

  // Progress stepper state
  readonly currentStep = signal(1);

  readonly steps: CheckoutStep[] = [
    {
      id: 1,
      label: 'Zip Code',
      icon: ImagePaths.STEP_1_ICON,
      route: '/purchase',
      width: 20,
      height: 16,
    },
    {
      id: 2,
      label: 'Choose a Plan',
      icon: ImagePaths.STEP_2_ICON,
      route: '/plan',
      width: 19,
      height: 19,
    },
    {
      id: 3,
      label: 'Checkout',
      icon: ImagePaths.STEP_3_ICON,
      route: '/payment',
      width: 19,
      height: 21,
    },
    {
      id: 4,
      label: 'Select Meals',
      icon: ImagePaths.STEP_4_ICON,
      width: 22,
      height: 22,
    },
  ];

  constructor() {
    this.updateStepFromUrl(this.router.url);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.updateStepFromUrl(event.urlAfterRedirects);
      });
  }

  protected onStepClick(step: CheckoutStep): void {
    // Check if step is accessible before allowing navigation
    if (!this.isStepAccessible(step.id)) {
      return;
    }

    this.currentStep.set(step.id);

    if (step.route) {
      this.router.navigate([step.route]);
    }
  }

  private isStepAccessible(stepId: number): boolean {
    switch (stepId) {
      case 1:
        // Step 1 (Zip Code) is always accessible
        return true;
      case 2:
        // Step 2 (Plan) requires zipcode
        return this.stepPrecondition.canAccessPlan();
      case 3:
        // Step 3 (Payment) requires plan data
        return this.stepPrecondition.canAccessPayment();
      case 4:
        // Step 4 (Select Meals) is never clickable
        return false;
      default:
        return false;
    }
  }

  protected isStepDisabled(stepId: number): boolean {
    return !this.isStepAccessible(stepId);
  }

  private updateStepFromUrl(url: string): void {
    const matchedStep = this.steps.find(
      (step) => step.route && url.startsWith(step.route),
    );

    if (matchedStep) {
      this.currentStep.set(matchedStep.id);
    }
  }
}
